export interface AnchorSpec {
  node?: string;
  selector?: string;
  structural?: {
    within: string;
    match: string;
    labels?: string[];
    labelWithin?: string;
    containsSelector?: string;
  };
  submenu?: {
    moduleLabels: string[];
    labels: string[];
  };
  group?: AnchorSpec[];
}

export type AnchorStrategy = 'node' | 'selector' | 'structural' | 'submenu' | 'group';

function nearestCommonAncestor(elements: HTMLElement[]): HTMLElement | null {
  if (elements.length === 0) return null;
  if (elements.length === 1) return elements[0];

  const chainOf = (el: Element): Element[] => {
    const chain: Element[] = [];
    let node: Element | null = el;
    while (node) {
      chain.push(node);
      node = node.parentElement;
    }
    return chain;
  };

  const [first, ...rest] = elements;
  let candidates = new Set(chainOf(first));

  for (const el of rest) {
    const chain = new Set(chainOf(el));
    for (const node of candidates) {
      if (!chain.has(node)) candidates.delete(node);
    }
  }
  if (candidates.size === 0) return null;

  for (const node of chainOf(first)) {
    if (candidates.has(node)) return node as HTMLElement;
  }
  return null;
}

export interface ResolvedAnchor {
  element: HTMLElement;
  strategy: AnchorStrategy;
}

function safeQueryAll(selector: string, root: ParentNode = document): Element[] {
  try {
    return Array.from(root.querySelectorAll(selector));
  } catch {
    return [];
  }
}

export function waitForDom<T>(probe: () => T | null, timeoutMs: number): Promise<T | null> {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      const hit = probe();
      if (hit !== null) return resolve(hit);
      if (Date.now() - startedAt >= timeoutMs) return resolve(null);
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}

export function accessibleLabel(el: Element): string {
  const aria = el.getAttribute('aria-label');
  if (aria && aria.trim()) return aria.trim();

  const img = el.querySelector('img[alt]');
  const alt = img?.getAttribute('alt');
  if (alt && alt.trim()) return alt.trim();

  return (el.textContent || '').trim();
}

export function isVisible(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  const style = window.getComputedStyle(el);
  return style.visibility !== 'hidden' && style.display !== 'none';
}

export function isOccluded(el: Element): boolean {
  let node: Element | null = el;
  while (node && node !== document.body) {
    if (node.getAttribute('aria-hidden') === 'true') return true;
    if (node.hasAttribute('inert')) return true;
    node = node.parentElement;
  }
  return false;
}

const VIEW_TOLERANCE = 4;

export function isOutOfView(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  if (rect.bottom <= VIEW_TOLERANCE || rect.top >= viewportHeight - VIEW_TOLERANCE) return true;
  if (rect.right <= VIEW_TOLERANCE || rect.left >= viewportWidth - VIEW_TOLERANCE) return true;

  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (!/auto|scroll|overlay/.test(`${style.overflowY} ${style.overflowX}`)) continue;
    const box = node.getBoundingClientRect();
    if (box.width === 0 && box.height === 0) continue;
    if (rect.bottom <= box.top + VIEW_TOLERANCE || rect.top >= box.bottom - VIEW_TOLERANCE) return true;
    if (rect.right <= box.left + VIEW_TOLERANCE || rect.left >= box.right - VIEW_TOLERANCE) return true;
  }

  return false;
}

export function isReachable(el: Element): boolean {
  return isVisible(el) && !isOccluded(el);
}

export function resolveAnchor(spec: AnchorSpec): ResolvedAnchor | null {
  const firstVisible = (selector: string): HTMLElement | null =>
    (safeQueryAll(selector).find(isVisible) as HTMLElement | undefined) ?? null;

  if (spec.node) {
    const el = firstVisible(`[data-tour-node="${CSS.escape(spec.node)}"]`);
    if (el) return { element: el, strategy: 'node' };
  }

  if (spec.selector) {
    const el = firstVisible(spec.selector);
    if (el) return { element: el, strategy: 'selector' };
  }

  if (spec.submenu) {
    const { moduleLabels, labels } = spec.submenu;
    const scope = accordionScopeFor(moduleLabels);
    if (scope) {
      for (const want of labels.map((l) => l.trim().toLowerCase()).filter(Boolean)) {
        for (const item of safeQueryAll(HOST.sidebarSubItem, scope)) {
          if (isVisible(item) && accessibleLabel(item).toLowerCase() === want) {
            return { element: item as HTMLElement, strategy: 'submenu' };
          }
        }
      }
    }
  }

  if (spec.structural) {
    const { within, match, labels = [], labelWithin, containsSelector } = spec.structural;
    const wanted = labels.map((l) => l.trim().toLowerCase()).filter(Boolean);

    const labelMatches = (candidate: Element, want: string): boolean => {
      if (!labelWithin) return accessibleLabel(candidate).toLowerCase() === want;
      return safeQueryAll(labelWithin, candidate).some(
        (inner) => (inner.textContent || '').trim().toLowerCase() === want,
      );
    };

    const candidates = () =>
      safeQueryAll(within).flatMap((container) => safeQueryAll(match, container));

    for (const want of wanted) {
      for (const candidate of candidates()) {
        if (isVisible(candidate) && labelMatches(candidate, want)) {
          return { element: candidate as HTMLElement, strategy: 'structural' };
        }
      }
    }

    if (containsSelector) {
      for (const candidate of candidates()) {
        if (isVisible(candidate) && safeQueryAll(containsSelector, candidate).length > 0) {
          return { element: candidate as HTMLElement, strategy: 'structural' };
        }
      }
    }
  }

  if (spec.group && spec.group.length > 0) {
    const elements: HTMLElement[] = [];
    for (const sub of spec.group) {
      const hit = resolveAnchor(sub)?.element;
      if (!hit) break;
      elements.push(hit);
    }
    if (elements.length === spec.group.length) {
      const container = nearestCommonAncestor(elements);
      if (container && isVisible(container)) {
        return { element: container, strategy: 'group' };
      }
    }
  }

  return null;
}


export const HOST = {
  sidebar: '[data-sidebar="sidebar"]',
  sidebarItem: '[data-sidebar="menu-button"]',
  sidebarMenuItem: '[data-sidebar="menu-item"]',
  sidebarAccordion: '[data-sidebar="menu-button"][data-state]',
  sidebarSubItem: '[data-sidebar="menu-sub-button"]',
  sidebarCard: '[role="button"]',
  nexaCardIcon: 'svg.lucide-bot',
  headerAiLauncher: '[title="NEXA AI (Beta)"]',
  workspaceSwitcher: '[data-tour="business-account-selector"]',
  accountMenuTrigger: '[data-testid="sidebar-account-footer"] [data-testid="user-menu-trigger"]',
  popperPanel: '[data-radix-popper-content-wrapper] > div',
  popperWrapper: '[data-radix-popper-content-wrapper]',
  workspaceCheckIcon: 'svg.lucide-check',
  addWorkspace: '[data-testid="sidebar-add-workspace"]',
  workspaceIntegrations: '[data-testid="sidebar-integrations"]',
  accountSettingsRow: '[data-testid="sidebar-account-settings"]',
  accountAuditLogRow: '[data-testid="sidebar-audit-log"]',
  accountGuideRow: '[data-testid="user-menu-getting-started"]',
  accountUpdatesRow: '[data-testid="user-menu-updates"]',
  accountSignOutRow: '[data-testid="user-menu-logout"]',
  sidebarOpen: ['[data-tour="sidebar-trigger"]', '[aria-label="Open navigation menu"]', '[data-sidebar="trigger"]'],
  sidebarClose: ['[aria-label="Close navigation menu"]', '[data-sidebar="trigger"]'],
  sidebarExpand: ['[aria-label="Expand sidebar"]', '[data-sidebar="trigger"]'],
  sidebarCollapse: ['[aria-label="Collapse sidebar"]', '[data-sidebar="trigger"]'],
  headerSlot: '[data-tour-slot="header-actions"]',
  suppressDuringTour: ['#wenext-sandbox-badge'],
} as const;

export function sidebarAnchor(label: string | string[], node?: string): AnchorSpec {
  return {
    node,
    structural: {
      within: HOST.sidebar,
      match: HOST.sidebarItem,
      labels: Array.isArray(label) ? label : [label],
    },
  };
}

function hoverPanelAnchor(identify: {
  labels?: string[];
  labelWithin?: string;
  containsSelector?: string;
}): AnchorSpec {
  return {
    structural: {
      within: 'body',
      match: HOST.popperPanel,
      labels: identify.labels,
      labelWithin: identify.labelWithin,
      containsSelector: identify.containsSelector,
    },
  };
}

export const WORKSPACE_MENU: AnchorSpec = hoverPanelAnchor({
  labels: ['Workspaces'],
  labelWithin: 'div',
  containsSelector: HOST.workspaceCheckIcon,
});

export const ACCOUNT_MENU: AnchorSpec = hoverPanelAnchor({
  containsSelector: HOST.accountSignOutRow,
});

export const CURRENT_WORKSPACE: AnchorSpec = {
  structural: {
    within: HOST.popperPanel,
    match: 'button',
    containsSelector: HOST.workspaceCheckIcon,
  },
};

export function accountMenuRowAnchor(label: string): AnchorSpec {
  return { structural: { within: HOST.popperPanel, match: 'button', labels: [label] } };
}

export function accordionTriggerFor(moduleLabels: string[]): HTMLElement | null {
  if (isSidebarIconCollapsed()) return null;
  const wanted = moduleLabels.map((l) => l.trim().toLowerCase()).filter(Boolean);
  for (const want of wanted) {
    for (const container of safeQueryAll(HOST.sidebar)) {
      for (const trigger of safeQueryAll(HOST.sidebarAccordion, container)) {
        if (isVisible(trigger) && accessibleLabel(trigger).toLowerCase() === want) {
          return trigger as HTMLElement;
        }
      }
    }
  }
  return null;
}

export function accordionScopeFor(moduleLabels: string[]): HTMLElement | null {
  const trigger = accordionTriggerFor(moduleLabels);
  if (!trigger) return null;
  return (trigger.closest(HOST.sidebarMenuItem) as HTMLElement | null) ?? trigger.parentElement;
}

export function isAccordionOpen(trigger: HTMLElement): boolean {
  const state = trigger.getAttribute('data-state');
  if (state) return state === 'open';
  const scope = trigger.closest(HOST.sidebarMenuItem) ?? trigger.parentElement;
  return scope ? safeQueryAll(HOST.sidebarSubItem, scope).some(isVisible) : false;
}

export function openAccordion(moduleLabels: string[]): HTMLElement | null {
  const trigger = accordionTriggerFor(moduleLabels);
  if (!trigger) return null;
  if (!isAccordionOpen(trigger)) trigger.click();
  return trigger;
}

export function openAccordions(): HTMLElement[] {
  return safeQueryAll(HOST.sidebar)
    .flatMap((c) => safeQueryAll(HOST.sidebarAccordion, c))
    .filter((el) => isVisible(el) && isAccordionOpen(el as HTMLElement)) as HTMLElement[];
}

export async function collapseAccordionsExcept(keepLabels: string[]): Promise<boolean> {
  if (isSidebarIconCollapsed()) return false;

  const keep = keepLabels.map((label) => label.trim().toLowerCase()).filter(Boolean);
  const stale = openAccordions().filter(
    (trigger) => !keep.includes(accessibleLabel(trigger).trim().toLowerCase()),
  );
  if (stale.length === 0) return false;

  stale.forEach((trigger) => trigger.click());
  await waitForDom(
    () => (stale.every((trigger) => !isAccordionOpen(trigger)) ? true : null),
    SIDEBAR_SHAPE_TIMEOUT_MS,
  );
  return true;
}


function sidebarShell(): HTMLElement | null {
  return (safeQueryAll(HOST.sidebar).find(isVisible) as HTMLElement | undefined) ?? null;
}

function sidebarStateHost(): HTMLElement | null {
  const shell = sidebarShell();
  return (shell?.closest('[data-state][data-side]') as HTMLElement | null) ?? null;
}

function isSidebarDrawer(): boolean {
  return sidebarShell()?.getAttribute('data-mobile') === 'true';
}

export function isSidebarIconCollapsed(): boolean {
  return sidebarStateHost()?.getAttribute('data-state') === 'collapsed';
}

export type SidebarShape = 'absent' | 'drawer' | 'rail' | 'rail-collapsed';

export function sidebarShape(): SidebarShape {
  if (!sidebarShell()) return 'absent';
  if (isSidebarDrawer()) return 'drawer';
  return isSidebarIconCollapsed() ? 'rail-collapsed' : 'rail';
}

export function isAnchorClipped(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  if (rect.top < 0 || rect.left < 0 || rect.bottom > viewportHeight || rect.right > viewportWidth) {
    return true;
  }

  for (let parent = el.parentElement; parent; parent = parent.parentElement) {
    const style = window.getComputedStyle(parent);
    if (!/auto|scroll|hidden|overlay/.test(`${style.overflowY} ${style.overflowX}`)) continue;
    const box = parent.getBoundingClientRect();
    if (box.width === 0 && box.height === 0) continue;
    if (rect.top < box.top || rect.bottom > box.bottom) return true;
    if (rect.left < box.left || rect.right > box.right) return true;
  }

  return false;
}

export function keepAnchorInView(el: HTMLElement): boolean {
  if (!isAnchorClipped(el)) return false;
  try {
    el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
  } catch {
    el.scrollIntoView?.();
  }
  return true;
}

function firstVisibleOf(selectors: readonly string[]): HTMLElement | null {
  for (const selector of selectors) {
    const hit = safeQueryAll(selector).find(isVisible) as HTMLElement | undefined;
    if (hit) return hit;
  }
  return null;
}

export interface SidebarRestore {
  openedDrawer: boolean;
  expandedRail: boolean;
}

const SIDEBAR_SHAPE_TIMEOUT_MS = 900;

export async function ensureSidebarUsable(): Promise<SidebarRestore | null> {
  const changed: SidebarRestore = { openedDrawer: false, expandedRail: false };

  if (!sidebarShell()) {
    firstVisibleOf(HOST.sidebarOpen)?.click();
    const appeared = await waitForDom(() => sidebarShell(), SIDEBAR_SHAPE_TIMEOUT_MS);
    if (!appeared) return null;
    changed.openedDrawer = true;
  }

  if (!isSidebarDrawer() && isSidebarIconCollapsed()) {
    firstVisibleOf(HOST.sidebarExpand)?.click();
    const expanded = await waitForDom(
      () => (isSidebarIconCollapsed() ? null : true),
      SIDEBAR_SHAPE_TIMEOUT_MS,
    );
    if (expanded) changed.expandedRail = true;
  }

  return changed.openedDrawer || changed.expandedRail ? changed : null;
}

export function anchorTargetsSidebar(anchor: AnchorSpec): boolean {
  if (anchor.submenu) return true;
  if (anchor.structural?.within === HOST.sidebar) return true;
  if (anchor.group?.some(anchorTargetsSidebar)) return true;

  const selectors = [anchor.node ? `[data-tour-node="${anchor.node}"]` : null, anchor.selector];
  return selectors.some((selector) =>
    selector ? safeQueryAll(selector).some((el) => el.closest(HOST.sidebar)) : false,
  );
}

export async function closeSidebarDrawer(): Promise<boolean> {
  if (!isSidebarDrawer()) return true;
  firstVisibleOf(HOST.sidebarClose)?.click();
  return (await waitForDom(() => (sidebarShell() ? null : true), SIDEBAR_SHAPE_TIMEOUT_MS)) === true;
}

export function restoreSidebarShape(restore: SidebarRestore | null): void {
  if (!restore) return;
  if (restore.expandedRail && !isSidebarIconCollapsed()) {
    firstVisibleOf(HOST.sidebarCollapse)?.click();
  }
  if (restore.openedDrawer && sidebarShell()) {
    firstVisibleOf(HOST.sidebarClose)?.click();
  }
}

export function sidebarCardAnchor(
  label: string | string[],
  node?: string,
  iconSelector?: string,
): AnchorSpec {
  return {
    node,
    structural: {
      within: HOST.sidebar,
      match: HOST.sidebarCard,
      labels: Array.isArray(label) ? label : [label],
      labelWithin: 'span',
      containsSelector: iconSelector,
    },
  };
}

export function submenuAnchor(
  moduleLabel: string | string[],
  subLabel: string | string[],
  node?: string,
): AnchorSpec {
  return {
    node,
    submenu: {
      moduleLabels: Array.isArray(moduleLabel) ? moduleLabel : [moduleLabel],
      labels: Array.isArray(subLabel) ? subLabel : [subLabel],
    },
  };
}
